import { useState, useEffect, useRef, useCallback } from 'react';

export interface SectionProgress {
  id: string;
  label: string;
  viewed: boolean;
  viewedAt?: number;
}

const STORAGE_KEY = 'rwa99_read_progress';

function loadProgress(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) {
    return {};
  }
}

function saveProgress(data: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (_e) {
    // ignore
  }
}

/**
 * Tracks which page sections have been scrolled into view.
 * Uses IntersectionObserver on elements with data-section-id attribute.
 * Returns a map of sectionId → timestamp when first viewed.
 */
export function useReadProgress(sectionIds: string[]) {
  const [viewed, setViewed] = useState<Record<string, number>>(() => loadProgress());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const markViewed = useCallback((id: string) => {
    setViewed((prev) => {
      if (prev[id]) return prev;
      const next = { ...prev, [id]: Date.now() };
      saveProgress(next);
      return next;
    });
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).dataset.sectionId;
            if (id) markViewed(id);
          }
        });
      },
      { threshold: 0.15 }
    );

    // Observe all target elements
    const observe = () => {
      sectionIds.forEach((id) => {
        const el = document.querySelector(`[data-section-id="${id}"]`);
        if (el) observerRef.current?.observe(el);
      });
    };

    // Retry a few times in case elements mount late
    observe();
    const t1 = setTimeout(observe, 800);
    const t2 = setTimeout(observe, 2000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observerRef.current?.disconnect();
    };
  }, [sectionIds, markViewed]);

  const resetProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setViewed({});
  }, []);

  const viewedCount = Object.keys(viewed).length;
  const totalCount = sectionIds.length;
  const progressPct = totalCount > 0 ? Math.round((viewedCount / totalCount) * 100) : 0;

  return { viewed, viewedCount, totalCount, progressPct, resetProgress };
}